// eslint-disable-next-line deprecation/deprecation
import { Capacitor, registerPlugin } from '@capacitor/core';
// eslint-disable-next-line @typescript-eslint/naming-convention
const ServiceWorker = registerPlugin('ServiceWorker');
// for compatibility, since the Android one is named ServiceWorkerPlugin for some reason
// eslint-disable-next-line @typescript-eslint/naming-convention
const ServiceWorkerAndroid = registerPlugin('ServiceWorkerPlugin');
export * from './definitions';
export { ServiceWorker, ServiceWorkerAndroid as ServiceWorkerPlugin };
// The following code initialises/polyfills a bunch of the classes needed for the iOS version of the plugin.
/**
 * This class implements our own version of the ServiceWorkerRegistration interface
 * of the ServiceWorker API. It represents the service worker registration.
 * Ref: https://developer.mozilla.org/en-US/docs/Web/API/ServiceWorkerRegistration
 */
class SWorkerRegistration {
    constructor(installing, waiting, active, scope) {
        this.installing = installing;
        this.waiting = waiting;
        this.active = active;
        this.scope = scope;
        this.uninstalling = false;
    }
}
// Ref: https://www.w3.org/TR/service-workers/#dom-serviceworker-postmessage
class SWorker {
    constructor(scriptURL) {
        this.scriptURL = scriptURL;
        this.state = 'activated';
    }
    // void postMessage(any message, optional sequence<object> transfer = []);
    postMessage(message) {
        // Serialize the message.
        const serializedMessage = JSON.stringify(message);
        // Send the message to native for delivery to the JSContext.
        ServiceWorker.postMessage({ message: serializedMessage });
    }
}
/**
 * This class is created to replace EventTarget.
 */
class EventDispatcher {
    constructor() {
        this._listeners = [];
    }
    hasEventListener(type, listener) {
        return this._listeners.some(item => item.type === type && item.listener === listener);
    }
    addEventListener(type, listener) {
        if (!this.hasEventListener(type, listener)) {
            this._listeners.push({ type, listener, options: { once: false } });
        }
    }
    removeEventListener(type, listener) {
        const index = this._listeners.findIndex(item => item.type === type && item.listener === listener);
        if (index !== -1) {
            this._listeners.splice(index, 1);
        }
    }
    removeEventListeners() {
        this._listeners = [];
    }
    dispatchEvent(evt) {
        this._listeners
            .filter(item => item.type === evt.type)
            .forEach(item => {
            const { type, listener, options: { once }, } = item;
            listener.call(this, evt);
            if (once === true) {
                this.removeEventListener(type, listener);
            }
        });
        return true;
    }
}
/**
 * According to https://developer.mozilla.org/en-US/docs/Web/API/ServiceWorkerContainer/controller,
 * the service worker container is inherit from EventTarget. However, we can't extend from
 * EventTarget because custom event target instance is not supported.
 * The name EventDispatcher is used explicitly to avoid overlapping with the browser EventTarget.
 *
 * Safari 14 supports custom EventTarget instance. However, the minimum supported version for
 * this plugin is iOS 11.
 * Ref: https://developer.apple.com/documentation/safari-release-notes/safari-14-release-notes
 */
class SWContainer extends EventDispatcher {
    get ready() {
        return new Promise((resolve, reject) => {
            ServiceWorker.serviceWorkerReady()
                .then(({ installing, waiting, scope, active }) => {
                this._scriptURL = active.scriptURL;
                const registration = new SWorkerRegistration(installing, waiting, new SWorker(active.scriptURL), scope);
                resolve(registration);
            })
                .catch(err => {
                console.error('Error when getting registration object from serviceWorker.ready: ', err);
                reject(err);
            });
        });
    }
    /**
     * Obtain the currently activated service worker object.
     */
    get controller() {
        return this._scriptURL ? new SWorker(this._scriptURL) : undefined;
    }
    register(scriptURL) {
        return new Promise((resolve, reject) => {
            ServiceWorker.register({ script: scriptURL })
                .then(({ installing, waiting, scope, active }) => {
                this._scriptURL = active.scriptURL;
                resolve(new SWorkerRegistration(installing, waiting, new SWorker(active.scriptURL), scope));
            })
                .catch(err => {
                console.error('Error when getting registration object from serviceWorker.register(): ', err);
                reject(err);
            });
        });
    }
    /**
     * Return ServiceWorkerRegistration object whose scope
     * URL matches the provided document URL.
     */
    getRegistration(scopeOfWorker) {
        return new Promise((resolve, reject) => {
            ServiceWorker.serviceWorkerReady()
                .then(({ installing, waiting, scope, active }) => {
                if (scopeOfWorker !== scope) {
                    console.warn(`
              NOTE: You are trying to call getRegistration() with scope ${scopeOfWorker} that is not belongs to any registered service worker.
              The iOS service worker only support on service worker instance at the moment.
            `);
                }
                this._scriptURL = active.scriptURL;
                const registration = new SWorkerRegistration(installing, waiting, new SWorker(active.scriptURL), scope);
                resolve(registration);
            })
                .catch((err) => {
                console.error('Error when getting registration object from serviceWorker.ready: ', err);
                reject(err);
            });
        });
    }
}
// Only polyfill the service worker container
// when we are running Capacitor on native iOS.
if (Capacitor.getPlatform() === 'ios' &&
    'navigator' in globalThis &&
    !('serviceWorker' in globalThis.navigator)) {
    navigator.serviceWorker = new SWContainer();
}
